import { Link } from "react-router-dom";
import { ScrollText, AlertOctagon, UserCheck, Mail } from "lucide-react";

const EFFECTIVE_DATE = "February 1, 2026";

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-fade-in">
      <p className="cm-label">TERMS OF SERVICE</p>
      <h1 className="cm-heading text-4xl sm:text-5xl lg:text-6xl mt-3">
        The <span className="text-copper">house rules</span>.
      </h1>
      <p className="mt-4 text-sm text-gray-500">
        Effective date: <time dateTime="2026-02-01">{EFFECTIVE_DATE}</time>
      </p>
      <p className="mt-6 text-gray-300 leading-relaxed">
        By creating an account, uploading a photo, or otherwise using
        HairMaxxing Pro (&ldquo;the Service&rdquo;), you agree to these
        Terms of Service. If you don&rsquo;t agree, please don&rsquo;t use
        the Service.
      </p>

      <section aria-labelledby="service" className="mt-12">
        <h2 id="service" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <ScrollText className="w-5 h-5 text-copper" aria-hidden="true" />
          The Service
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          HairMaxxing Pro analyzes a photo of a haircut and returns
          recommendations: a haircut name, a product routine, and a script
          to read to a barber. The recommendations are{" "}
          <strong className="text-platinum">cosmetic suggestions
          only</strong> &mdash; they are not professional advice and are
          not a substitute for the judgment of a licensed barber, stylist,
          dermatologist, or other professional.
        </p>
      </section>

      <section aria-labelledby="as-is" className="mt-10">
        <h2 id="as-is" className="cm-heading text-2xl text-platinum">
          Service provided &ldquo;as is&rdquo;
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          The Service is provided &ldquo;as is&rdquo; and &ldquo;as
          available,&rdquo; without warranty of any kind &mdash; express
          or implied &mdash; including but not limited to the warranties
          of merchantability, fitness for a particular purpose,
          accuracy, or non-infringement. We do not warrant that the
          results will match your hair, your face shape, your barber&rsquo;s
          interpretation, or your desired outcome.
        </p>
      </section>

      <section aria-labelledby="age" className="mt-10">
        <h2 id="age" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <UserCheck className="w-5 h-5 text-copper" aria-hidden="true" />
          Age requirement
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          You must be at least <strong className="text-platinum">18 years
          old</strong> to use the Service, or have the consent of a
          parent or legal guardian who agrees to these Terms on your
          behalf. If we learn that we have collected data from a person
          under 18 without proper guardian consent, we will delete it.
        </p>
      </section>

      <section aria-labelledby="acceptable" className="mt-10">
        <h2 id="acceptable" className="cm-heading text-2xl text-platinum">
          Acceptable use
        </h2>
        <ul className="mt-3 space-y-2 text-gray-300 list-disc list-inside marker:text-copper leading-relaxed">
          <li>
            Only upload photos that you have the right to upload &mdash;
            don&rsquo;t upload photos of other people without their
            consent.
          </li>
          <li>
            Don&rsquo;t attempt to circumvent rate limits, abuse the
            payment system, or use the Service to infringe anyone&rsquo;s
            rights.
          </li>
          <li>
            Don&rsquo;t attempt to extract, reverse-engineer, or scrape
            our AI models or data.
          </li>
        </ul>
      </section>

      <section aria-labelledby="liability" className="mt-10 cm-card p-6 border-copper/40">
        <h2 id="liability" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <AlertOctagon className="w-5 h-5 text-copper" aria-hidden="true" />
          Limitation of liability
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          To the maximum extent permitted by law, HairMaxxing Pro, its
          owners, employees, and affiliates will not be liable for any
          indirect, incidental, special, consequential, or punitive
          damages &mdash; including but not limited to a haircut you
          dislike, a barber miscommunication, lost profits, loss of data,
          or loss of goodwill &mdash; arising from your use of the
          Service. Our total cumulative liability to you for any claim
          arising from the Service will not exceed the greater of
          <strong className="text-platinum"> $20 USD</strong> or the total
          amount you have paid us in the past 12 months.
        </p>
      </section>

      <section aria-labelledby="payments" className="mt-10">
        <h2 id="payments" className="cm-heading text-2xl text-platinum">
          Payments and refunds
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          Top-up scan packs are billed through Stripe. Because scans are
          a digital service delivered immediately upon use, purchases are
          generally non-refundable once any scan in the pack has been
          used. If you believe a charge is in error, email
          support@hairmaxxingpro.com within 30 days and we&rsquo;ll work
          with you in good faith.
        </p>
      </section>

      <section aria-labelledby="termination" className="mt-10">
        <h2 id="termination" className="cm-heading text-2xl text-platinum">
          Account termination
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          You can delete your account at any time from the{" "}
          <Link to="/account" className="text-copper underline hover:text-copper/80">
            Account page
          </Link>
          . We may suspend or terminate accounts that violate these
          Terms or applicable law.
        </p>
      </section>

      <section aria-labelledby="law" className="mt-10">
        <h2 id="law" className="cm-heading text-2xl text-platinum">
          Governing law
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          These Terms are governed by the laws of the State of
          California, United States, without regard to its conflict-of-laws
          rules. Any dispute will be brought exclusively in the state or
          federal courts located in San Francisco County, California, and
          you consent to personal jurisdiction there.
        </p>
      </section>

      <section aria-labelledby="changes" className="mt-10">
        <h2 id="changes" className="cm-heading text-2xl text-platinum">
          Changes to these Terms
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          We may update these Terms from time to time. If we make
          material changes, we&rsquo;ll update the effective date above
          and, where appropriate, notify you in-app or by email. Your
          continued use of the Service after the update means you accept
          the new Terms.
        </p>
      </section>

      <section aria-labelledby="contact" className="mt-10 cm-card p-6 border-copper/30">
        <h2 id="contact" className="cm-heading text-2xl text-platinum flex items-center gap-3">
          <Mail className="w-5 h-5 text-copper" aria-hidden="true" />
          Contact
        </h2>
        <p className="mt-3 text-gray-300 leading-relaxed">
          Questions about these Terms?
        </p>
        <a
          href="mailto:support@hairmaxxingpro.com?subject=Terms%20Question"
          className="cm-btn-primary inline-flex mt-5"
          data-testid="terms-contact-link"
        >
          support@hairmaxxingpro.com
        </a>
      </section>

      <div className="mt-12 flex flex-wrap gap-4">
        <Link to="/" className="cm-btn-secondary inline-flex" data-testid="terms-back-home">
          Back to home
        </Link>
        <Link to="/privacy" className="cm-btn-secondary inline-flex" data-testid="terms-to-privacy">
          Read Privacy Policy
        </Link>
      </div>
    </div>
  );
}
